import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import CaptionGroup from '@ui/captiongroup/CaptionGroup';
import KpiStrip, { KpiStripItem } from '@ui/chart/KpiStrip';
import FormInputDatePicker from '@ui/form/input/FormInputDatePicker';
import Message from '@ui/message/Message';
import DashboardPlaceholder from '@ui/placeholder/DashboardPlaceholder';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import { PickupPeriod, VatBasis } from '@app/model/bi/Bi';
import BiService, { saveFile } from '@app/model/bi/service/BiService';
import {
	usePickupAlertSetting, usePickupBookings, usePickupMonitoring, usePropertyGroups, useSavePickupAlertSetting,
} from '@app/model/bi/service/BiHooks';
import useToaster from '@app/component/overlays/toaster/ToasterHook';
import BiPageHeader from '../fragment/BiPageHeader';
import BiSwitchRow, { optionsCaption } from '../fragment/BiSwitchRow';
import { AlertsCard, BookingsCard, StayMonthCard, SummaryCard } from '../fragment/PickupCards';
import {
	addDays, adrOf, bookedText, customPeriodOk, DEFAULT_ALERT, isPeriod, kindsOf, MatrixBasis, MAX_BOOK_DAYS, PERIODS, pickupBookingsFileName, pickupFileName,
	revenuePicker, showsHistoryNote,
} from '../fragment/PickupMonitoring.utilities';
import { formatDateTime, propertyCount, revenueName, shortName } from '../Bi.utilities';
import { useAccountBiScope, useBiAddress } from '../BiScopeHooks';

// Pick-up monitoring (BI, ROO2-314 Part 2): the bookings received, changed or cancelled in a booked period, for one
// property, a Property group or All properties: the KPI strip, the summary and the stay-month table with its Excel file
// (step 2a), the ADR alerts and the bookings list with its Excel files (step 2b). The figures come from findPickupMonitoring
// (the shared new-bookings rule of Part 1); the bookings from findPickupBookings, which loads at the same time. The only
// write is the user's ADR alert setting.

const PHONE = '(max-width: 639px)';
const usePhone = () => {
	const [phone, setPhone] = useState(() => typeof window !== 'undefined' && window.matchMedia(PHONE).matches);
	useEffect(() => {
		const media = window.matchMedia(PHONE);
		const on = () => setPhone(media.matches);
		media.addEventListener('change', on);
		return () => media.removeEventListener('change', on);
	}, []);
	return phone;
};
// GraphQLExecutor rejects with the message of the first GraphQL error (a string).
const reasonOf = (e: unknown): string => typeof e === 'string' ? e : (e instanceof Error ? e.message : 'Try again.');

const BiPickupMonitoringPage = () => {
	const scope = useAccountBiScope();
	const address = useBiAddress(scope.data);
	const [params] = useSearchParams();
	const phone = usePhone();
	const toaster = useToaster();
	const propertyGroups = usePropertyGroups();
	const [exporting, setExporting] = useState(false);
	const [exportingBookings, setExportingBookings] = useState(false);
	const alertSetting = usePickupAlertSetting();
	const saveAlert = useSavePickupAlertSetting();
	const setting = alertSetting.data ?? DEFAULT_ALERT;
	// D24: the booked period in the address (?booked=…; Custom: &bookFrom=…&bookTo=…), default Today (D14).
	const bookedParam = params.get('booked')?.toUpperCase() ?? null;
	const period: PickupPeriod = isPeriod(bookedParam) ? bookedParam : 'TODAY';
	const bookFrom = period === 'CUSTOM' ? params.get('bookFrom') : null;
	const bookTo = period === 'CUSTOM' ? params.get('bookTo') : null;
	// D40: on this page the revenue starts at Room only + Net (excl. VAT); ?vat=incl gives Incl. VAT.
	const vat: VatBasis = params.get('vat') === 'incl' ? 'INCL' : 'NET';
	const revenueScope = address.revenueScope;
	const revenue = useMemo(() => revenuePicker(revenueScope, vat), [revenueScope, vat]);
	const today = scope.data?.asOf ?? new Date().toISOString().slice(0, 10);
	const customOk = period !== 'CUSTOM' || customPeriodOk(bookFrom, bookTo, today);
	const enabled = scope.data !== undefined && address.ready && customOk;
	const report = usePickupMonitoring(address.companyHotelId, address.propertyGroupId, period, bookFrom, bookTo, address.includeOptions, enabled);
	const data = report.data;
	const bookings = usePickupBookings(address.companyHotelId, address.propertyGroupId, period, bookFrom, bookTo, address.includeOptions, enabled);
	// The two date fields of Custom follow the address: a new address sets them, a picked day sets the address. A custom
	// period with no days (or the first click on Custom) starts at the last 7 days to today in the company time zone.
	const form = useForm<{ bookFrom: string | null, bookTo: string | null }>({ defaultValues: { bookFrom, bookTo } });
	const pickedFrom = form.watch('bookFrom');
	const pickedTo = form.watch('bookTo');
	const scopeToday = scope.data?.asOf ?? null;
	useEffect(() => {
		if (period !== 'CUSTOM' || scopeToday === null) return;
		if (bookFrom === null || bookTo === null) address.set({ bookFrom: addDays(scopeToday, -6), bookTo: scopeToday });
		else if (form.getValues('bookFrom') !== bookFrom || form.getValues('bookTo') !== bookTo) form.reset({ bookFrom, bookTo });
	}, [period, scopeToday, bookFrom, bookTo, address, form]);
	useEffect(() => {
		if (period !== 'CUSTOM' || bookFrom === null || bookTo === null) return;
		const from = typeof pickedFrom === 'string' ? pickedFrom.slice(0, 10) : '';
		const to = typeof pickedTo === 'string' ? pickedTo.slice(0, 10) : '';
		if (from !== '' && to !== '' && (from !== bookFrom || to !== bookTo)) address.set({ bookFrom: from, bookTo: to });
	}, [period, pickedFrom, pickedTo, bookFrom, bookTo, address]);
	const kinds = useMemo(() => data === undefined ? null : kindsOf(data.properties, revenue), [data, revenue]);

	if (scope.isError) return <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
		<Message state="error" title="Pick-up monitoring did not load" flush>Reload the page. If the problem stays, contact RoomPulse.</Message>
	</main>;
	const s = scope.data;
	if (s === undefined) return <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8"><DashboardPlaceholder /></main>;

	const all = address.companyHotelId === null;
	const scopeKind = !all ? 'property' as const : address.propertyGroup !== null ? 'group' as const : 'all' as const;
	const sublabel = all ? `${propertyCount(address.properties.length)} · Pick-up monitoring` : `${address.property?.area ?? ''} · Pick-up monitoring`;
	const sources = [...new Set(address.properties.map(p => p.reservationsSource).filter((v): v is string => v !== null))];
	const nameOf = (id: number) => {
		const property = s.properties.find(x => x.companyHotelId === id);
		return property === undefined ? `Property ${id}` : shortName(property, s.companyName);
	};
	// The table lists the properties of the answer: a property with no Reservations data (no PMS import) has no block.
	const answered = new Set((data?.properties ?? []).map(p => p.companyHotelId));
	const names = address.properties.filter(p => answered.has(p.companyHotelId)).map(p => ({ companyHotelId: p.companyHotelId, name: shortName(p, s.companyName) }));
	const periodText = data === undefined ? '' : bookedText(data.bookFrom, data.bookTo);
	const label = scopeKind === 'property' ? 'Total' : scopeKind === 'group' ? 'Group total' : 'All properties';
	const empty = data !== undefined && (data.properties.length === 0 || data.properties.every(p => p.changes.length === 0));
	const state = !customOk || report.isError ? 'error' as const : data === undefined ? 'loading' as const : empty ? 'empty' as const
		: report.isPlaceholderData ? 'refreshing' as const : 'default' as const;
	const changed = kinds?.net.bookings ?? 0;
	const line = data === undefined ? null : `${changed.toLocaleString('en-US')} ${changed === 1 ? 'booking' : 'bookings'} received, changed or cancelled · ${
		scopeKind === 'property' ? nameOf(address.companyHotelId as number) : propertyCount(data.properties.length)}`;
	const v = (x: number | null) => kinds === null || state === 'loading' || state === 'error' ? null : x;
	const kpis: KpiStripItem[] = [
		{ id: 'new', label: 'New bookings', value: v(kinds?.fresh.bookings ?? null), format: 'number' },
		{ id: 'rn', label: 'New room nights', value: v(kinds?.fresh.roomNights ?? null), format: 'number' },
		{ id: 'adr', label: phone ? 'New ADR' : 'ADR of new bookings', value: v(kinds === null ? null : adrOf(kinds.fresh)), format: 'money', decimals: 2 },
		{ id: 'cancelled', label: 'Cancelled RN', value: v(kinds === null ? null : -Math.abs(kinds.canc.roomNights)), format: 'number' },
		{ id: 'net', label: 'Net pick-up RN', value: v(kinds?.net.roomNights ?? null), format: 'number' },
		{ id: 'netRev', label: 'Net pick-up revenue', value: v(kinds?.net.revenue ?? null), format: 'money' },
	];
	const retry = () => void report.refetch();
	const scopeName = scopeKind === 'property' ? nameOf(address.companyHotelId as number) : address.propertyGroup?.title ?? null;
	const onExport = (year: string, basis: MatrixBasis) => {
		if (data === undefined) return;
		setExporting(true);
		BiService.downloadPickupStayMonthExcel({ companyHotelId: address.companyHotelId, propertyGroupId: address.propertyGroupId, period, bookFrom, bookTo, year,
			show: basis, scope: revenueScope, vat, includeOptions: address.includeOptions })
			.then(blob => saveFile(blob, pickupFileName(scopeName, basis, year, data.asOf)))
			.catch(() => toaster.error('Export', 'The Excel file did not download. Try again. If the problem stays, contact RoomPulse.'))
			.finally(() => setExporting(false));
	};
	const bookingsState = !customOk || bookings.isError ? 'error' as const : bookings.data === undefined ? 'loading' as const
		: bookings.isPlaceholderData ? 'refreshing' as const : 'default' as const;
	const retryBookings = () => void bookings.refetch();
	const onExportBookings = (file: 'CURRENT_VIEW' | 'ALL_DATA') => {
		const answer = bookings.data;
		if (answer === undefined) return;
		setExportingBookings(true);
		BiService.downloadPickupBookingsExcel({ companyHotelId: address.companyHotelId, propertyGroupId: address.propertyGroupId, period, bookFrom, bookTo, file,
			scope: revenueScope, vat, includeOptions: address.includeOptions })
			.then(blob => saveFile(blob, pickupBookingsFileName(scopeName, file, answer.bookFrom, answer.bookTo, answer.asOf)))
			.catch(() => toaster.error('Export', 'The Excel file did not download. Try again. If the problem stays, contact RoomPulse.'))
			.finally(() => setExportingBookings(false));
	};
	// D9: Save stores the setting in the user's account; the cards keep the controls until it answers.
	const onSave = (next: typeof setting) => saveAlert.mutate(next, {
		onSuccess: () => toaster.success('ADR alerts', 'RoomPulse saved the alert setting in your account. It applies to every property.'),
		onError: error => toaster.error('ADR alerts', reasonOf(error as unknown)),
	});
	const many = all;
	const caption = `Revenue = ${revenueName(revenueScope, vat)}. ADR = revenue ÷ room nights; no ADR for the changes and the net (a mix of plus and minus). ${
		optionsCaption(address.options)}`;

	return <>
		<BiPageHeader scope={s} companyHotelId={address.companyHotelId} propertyGroup={address.propertyGroup} groups={address.groups}
			canEditGroups={address.canEditGroups} search={address.search} sublabel={sublabel} page="Pick-up monitoring">
			{address.importedAt !== null && <span className="whitespace-nowrap text-sm text-gray-500">Reservations as of{' '}
				<span className="font-semibold text-gray-900">{formatDateTime(address.importedAt)}</span></span>}
		</BiPageHeader>
		<main className="mx-auto flex max-w-7xl flex-col gap-5 px-4 py-6 sm:px-6 lg:px-8">
			<div className="flex flex-col gap-2">
				<BiSwitchRow scope={revenueScope} vat={vat} onScope={x => address.set({ revenue: x === 'TOTAL' ? 'total' : 'room' })}
					onVat={x => address.set({ vat: x === 'NET' ? 'net' : 'incl' })} options={address.options} onOptions={address.setOptions}
					line={line} sources={sources}>
					<CaptionGroup caption="Booked" className="col-span-2 w-full sm:col-span-1 sm:w-auto">
						<div className="w-full min-w-0"><ButtonTabs label="Book days of the page" options={PERIODS} value={period}
							onChange={x => address.set(x === 'CUSTOM'
								? { booked: 'custom', bookFrom: bookFrom ?? addDays(today, -6), bookTo: bookTo ?? today }
								: { booked: x.toLowerCase() })} /></div>
					</CaptionGroup>
				</BiSwitchRow>
				{period === 'CUSTOM' && <div className="relative z-20 grid grid-cols-2 gap-2 sm:flex sm:items-end sm:gap-3">
					<div className="w-full sm:w-40"><FormInputDatePicker form={form} name="bookFrom" label="Booked from" maxDate={new Date(`${today}T12:00:00`)} /></div>
					<div className="w-full sm:w-40"><FormInputDatePicker form={form} name="bookTo" label="Booked to" maxDate={new Date(`${today}T12:00:00`)} /></div>
					<p className="col-span-2 pb-2 text-xs text-gray-500">Up to {MAX_BOOK_DAYS} book days, today at the latest.</p>
				</div>}
			</div>
			{!customOk && <Message state="error" flush>A custom period has 1 to {MAX_BOOK_DAYS} book days, from the first day to the last day, today at the latest.</Message>}
			{report.isError && <Message state="error" title="The figures did not load" flush>{reasonOf(report.error as unknown)}</Message>}
			<div aria-busy={state === 'refreshing'} className={`transition-opacity${state === 'refreshing' ? ' opacity-50' : ''}`}>
				<KpiStrip ariaLabel="Pick-up: headline figures" items={kpis} state={state === 'loading' ? 'loading' : 'default'} />
			</div>
			<SummaryCard kinds={kinds} label={label} period={periodText} phone={phone} state={state} caption={caption} onRetry={retry} />
			{state !== 'empty' && <StayMonthCard properties={data?.properties ?? []} names={names} scope={scopeKind} groups={propertyGroups.data?.groups ?? []}
				revenue={revenue} revenueName={revenueName(revenueScope, vat).replace(/^./, c => c.toUpperCase())} period={periodText} asOf={data?.asOf ?? today}
				phone={phone} state={state} exporting={exporting} onRetry={retry} onExport={onExport} />}
			{state !== 'empty' && <AlertsCard answer={bookings.data} setting={setting} onSave={onSave} saving={saveAlert.isPending} revenue={revenue}
				many={many} nameOf={nameOf} period={periodText} phone={phone} state={bookingsState} onRetry={retryBookings} />}
			{state !== 'empty' && <BookingsCard answer={bookings.data} setting={setting} revenue={revenue} many={many} nameOf={nameOf}
				scopeName={scopeName ?? 'All properties'} period={periodText} phone={phone} state={bookingsState} exporting={exportingBookings}
				onRetry={retryBookings} onExport={onExportBookings} />}
			{data !== undefined && showsHistoryNote(data.bookFrom) && <p className="text-xs text-gray-500">
				The change history of the bookings starts on 19/10/2026: before that day, a change of an older booking is not visible.</p>}
		</main>
	</>;
};

export default BiPickupMonitoringPage;
